"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

/**
 * Forces an instant scroll to the top of the page on FORWARD navigation.
 *
 * Why this is needed: `html { scroll-behavior: smooth }` (set globally for
 * in-page anchor jumps) interferes with the App Router's scroll-to-top on route
 * change, so clicking a link can leave you mid-page. We temporarily flip
 * scroll-behavior to `auto`, jump to the top, then restore it.
 *
 * Back/forward navigations are left alone so the browser/router can restore the
 * previous scroll position; anchor navigations (URL with a hash) are also
 * skipped so the in-page jump still works.
 */
export function ScrollToTop() {
  const pathname = usePathname();
  const isPop = useRef(false);

  useEffect(() => {
    const onPop = () => {
      isPop.current = true;
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (isPop.current) {
      isPop.current = false; // back/forward → keep the restored position
      return;
    }
    if (window.location.hash) return; // anchor navigation → let the browser jump

    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    window.scrollTo(0, 0);
    html.style.scrollBehavior = prev;
  }, [pathname]);

  return null;
}
